import { AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.js";
import { type AuthConfig, authApi } from "../lib/api.js";

export function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void authApi
      .config()
      .then(setConfig)
      .catch(() => setConfig({ microsoftEnabled: false, localLoginEnabled: true }));
  }, []);

  async function handleLocalLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.loginLocal(username, password);
      await refresh();
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }

  function handleMsLogin() {
    window.location.href = "/api/auth/ms/login";
  }

  // Dev-Anmeldung ohne Passwort: setzt das Dev-Token, das die Backend-Middleware
  // NUR bei NODE_ENV=development/test akzeptiert. `import.meta.env.DEV` ist im
  // Produktions-Build false — Vite entfernt diesen Block dort vollstaendig.
  function handleDevLogin(rolle: "dev-admin" | "dev-hrbp" | "dev-manager") {
    localStorage.setItem("auth_token", rolle);
    window.location.href = "/";
  }

  function toggleLang() {
    const next = i18n.language === "de" ? "en" : "de";
    void i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  }

  // Hub-Modus: kein eigenes Login — der Agent Hub macht das SSO. Diese Seite
  // sieht man hinter dem Hub normalerweise nie (Identität kommt per Header);
  // erscheint sie doch, hilft nur ein Neuladen über den Hub.
  if (config?.mode === "hub") {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-4"
        style={{ background: "var(--bg-subtle)", color: "var(--text-muted)" }}
      >
        <img
          src="/logo-mindsquare.png"
          alt="mindsquare"
          className="h-10"
          style={{ objectFit: "contain" }}
        />
        <div className="text-sm max-w-sm">
          Die Anmeldung läuft über den mindsquare Hub. Bitte öffne die Anwendung erneut über das
          Hub-Launchpad.
        </div>
        <a href="/" className="text-xs font-medium" style={{ color: "var(--accent)" }}>
          Erneut laden
        </a>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ background: "var(--bg-subtle)" }}
    >
      {/* Language toggle */}
      <div className="absolute top-5 right-6">
        <button
          type="button"
          onClick={toggleLang}
          className="px-3 py-1.5 text-xs font-mono-data"
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            color: "var(--text-muted)",
            background: "var(--bg-card)",
            transition: "border-color var(--dur) var(--ease)",
          }}
        >
          {i18n.language === "de" ? "DE / EN" : "EN / DE"}
        </button>
      </div>

      <div className="w-full max-w-sm">
        {/* Logo + title */}
        <div className="flex flex-col items-center mb-8 gap-4">
          <img
            src="/logo-mindsquare.png"
            alt="mindsquare"
            className="h-10"
            style={{ objectFit: "contain" }}
          />
          <div className="text-center">
            <div
              className="text-xl font-bold tracking-tight"
              style={{ color: "var(--text-primary)" }}
            >
              {t("login.title")}
            </div>
            <div
              className="text-xs font-bold tracking-widest uppercase mt-1"
              style={{ color: "var(--accent)", letterSpacing: "0.12em" }}
            >
              HR Analytics
            </div>
          </div>
        </div>

        {/* Login card — Markenlinie oben: accent → gold */}
        <div
          className="card p-6 flex flex-col gap-4 relative overflow-hidden"
          style={{
            background: "var(--bg-card)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <div
            aria-hidden
            className="absolute inset-x-0 top-0"
            style={{
              height: 3,
              background: "linear-gradient(90deg, var(--accent), var(--accent-gold))",
              borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
            }}
          />
          {config?.microsoftEnabled && (
            <>
              <button
                type="button"
                onClick={handleMsLogin}
                className="w-full flex items-center justify-center gap-3 py-2.5 text-sm font-bold"
                style={{
                  background: "var(--bg-subtle)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  transition: "background var(--dur) var(--ease)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--bg-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--bg-subtle)";
                }}
              >
                <MicrosoftLogo />
                {t("login.microsoft", { defaultValue: "Mit Microsoft anmelden" })}
              </button>

              <div className="flex items-center gap-3 my-1">
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {t("login.or", { defaultValue: "oder" })}
                </span>
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              </div>
            </>
          )}

          <form onSubmit={handleLocalLogin} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold" style={{ color: "var(--text-secondary)" }}>
                {t("login.email", { defaultValue: "Benutzername" })}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="w-full px-3 py-2.5 text-sm"
                style={{
                  background: "var(--bg-subtle)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--text-primary)",
                  transition: "border-color var(--dur) var(--ease)",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "var(--border)";
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold" style={{ color: "var(--text-secondary)" }}>
                {t("login.password")}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full px-3 py-2.5 text-sm"
                style={{
                  background: "var(--bg-subtle)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--text-primary)",
                  transition: "border-color var(--dur) var(--ease)",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "var(--border)";
                }}
              />
            </div>

            {error && (
              <div
                className="flex items-center gap-2 px-3 py-2 text-xs"
                style={{
                  background: "var(--risk-high-bg)",
                  color: "var(--risk-high-text)",
                  border: "1px solid color-mix(in srgb, var(--risk-high) 20%, transparent)",
                  borderRadius: "var(--radius)",
                }}
              >
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="mt-1 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold disabled:opacity-50"
              style={{
                background: loading ? "var(--bg-subtle)" : "var(--accent)",
                color: loading ? "var(--text-muted)" : "#ffffff",
                borderRadius: "var(--radius)",
                transition: "background var(--dur) var(--ease), opacity var(--dur) var(--ease)",
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.background = "var(--accent-hover)";
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.background = "var(--accent)";
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> {t("common.loading")}
                </>
              ) : (
                <>
                  {t("login.submit")} <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs" style={{ color: "var(--text-muted)" }}>
            {config?.microsoftEnabled
              ? t("login.hintMs", {
                  defaultValue: "Microsoft-Konto fuer SSO -- lokaler Admin als Fallback",
                })
              : t("login.hint", { defaultValue: "Lokaler Admin-Login" })}
          </p>

          {/* Nur in der lokalen Entwicklung: Anmeldung ohne Passwort, damit
              Vorfuehrungen nicht an einem vergessenen Hash scheitern. Im
              Produktions-Build entfernt Vite diesen Block (import.meta.env.DEV). */}
          {import.meta.env.DEV && (
            <div
              className="flex flex-col gap-2 pt-4"
              style={{ borderTop: "1px dashed var(--border)" }}
            >
              <span
                className="text-[10px] font-bold tracking-widest uppercase text-center"
                style={{ color: "var(--text-muted)" }}
              >
                Nur lokal — ohne Passwort
              </span>
              <div className="flex gap-2">
                {(
                  [
                    ["dev-admin", "Admin"],
                    ["dev-hrbp", "HRBP"],
                    ["dev-manager", "Manager"],
                  ] as const
                ).map(([rolle, label]) => (
                  <button
                    key={rolle}
                    type="button"
                    onClick={() => handleDevLogin(rolle)}
                    className="flex-1 py-2 text-xs font-medium"
                    style={{
                      background: "var(--bg-subtle)",
                      border: "1px dashed var(--border)",
                      borderRadius: "var(--radius)",
                      color: "var(--text-secondary)",
                      cursor: "pointer",
                      transition: "background var(--dur) var(--ease)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--bg-hover)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "var(--bg-subtle)";
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-6" style={{ color: "var(--text-muted)" }}>
          mindsquare AG
        </p>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}
